import React from 'react';
import { 
  X, Network, Users, Shield, AlertCircle, Info, 
  RotateCcw, Archive, CheckCircle, Globe, ArrowRight,
  Settings, Eye, DollarSign, BookOpen,
  Banknote
} from 'lucide-react';

const GuideInterSiteDetail = ({ isOpen, onClose, interSiteInfo, famille }) => {
  if (!isOpen) return null;

  const isReference = interSiteInfo?.is_intersite && interSiteInfo?.is_reference;
  const isDistant = interSiteInfo?.is_intersite && !interSiteInfo?.is_reference;
  const wasReference = !interSiteInfo?.is_intersite && interSiteInfo?.was_intersite_reference;
  const wasDistant = !interSiteInfo?.is_intersite && interSiteInfo?.was_intersite_distant;
  const hubActive = interSiteInfo?.hub_is_active;

  const nbSitesDistants = (interSiteInfo?.nb_sites || 1) - 1;

  let statusLabel, statusColor, statusBg, statusBorder, StatusIcon;

  if (isReference) {
    statusLabel = 'Site de référence - Inter-site actif';
    statusColor = 'text-[#246990]';
    statusBg = 'bg-blue-50';
    statusBorder = 'border-blue-200';
    StatusIcon = Info;
  } else if (isDistant) {
    statusLabel = 'Site distant - Inter-site actif';
    statusColor = 'text-teal-700';
    statusBg = 'bg-teal-50';
    statusBorder = 'border-teal-200';
    StatusIcon = Globe;
  } else if (wasReference) {
    statusLabel = 'Ancien site de référence - Liaison désactivée';
    statusColor = 'text-amber-700';
    statusBg = 'bg-amber-50';
    statusBorder = 'border-amber-200';
    StatusIcon = RotateCcw;
  } else if (wasDistant && hubActive) {
    statusLabel = 'Ancien site distant - Re-liaison disponible';
    statusColor = 'text-green-700';
    statusBg = 'bg-green-50';
    statusBorder = 'border-green-200';
    StatusIcon = CheckCircle;
  } else if (wasDistant) {
    statusLabel = 'Ancien site distant - En attente';
    statusColor = 'text-gray-600';
    statusBg = 'bg-gray-50';
    statusBorder = 'border-gray-200';
    StatusIcon = Network;
  } else {
    statusLabel = 'Famille locale';
    statusColor = 'text-gray-600';
    statusBg = 'bg-gray-50';
    statusBorder = 'border-gray-200';
    StatusIcon = Users;
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
        
        {/* Header */}
        <div className={`p-4 ${statusBg} ${statusBorder} border-b rounded-t-xl flex items-center justify-between flex-shrink-0`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isReference || wasReference ? 'bg-[#246990]' : isDistant ? 'bg-teal-600' : wasDistant && hubActive ? 'bg-green-600' : 'bg-gray-500'}`}>
              <StatusIcon className="text-white" size={18} />
            </div>
            <div>
              <h2 className={`text-base font-bold ${statusColor}`}>{famille?.nom}</h2>
              <p className="text-xs text-gray-500">{statusLabel}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/50 rounded-lg transition-colors">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Contenu */}
        <div className="p-5 overflow-y-auto flex-1 space-y-4">

          {/* ═══════════════════════════════════════ */}
          {/* CAS 1 : Site de référence — actif      */}
          {/* ═══════════════════════════════════════ */}
          {isReference && (
            <>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-700 leading-relaxed">
                  <strong>Votre site pilote cette famille inter-site.</strong> Les réductions famille que vous définissez ici sont propagées automatiquement vers tous les sites distants connectés. Une réduction personnalisée reste propre à un élève et remplace la réduction famille pour cet élève.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-white border border-gray-200 rounded-lg text-center">
                  <div className="text-xl font-bold text-[#246990]">{interSiteInfo.nb_membres_total}</div>
                  <div className="text-[10px] text-gray-500">élèves total</div>
                </div>
                <div className="p-3 bg-white border border-gray-200 rounded-lg text-center">
                  <div className="text-xl font-bold text-[#246990]">{interSiteInfo.nb_sites}</div>
                  <div className="text-[10px] text-gray-500">sites connectés</div>
                  <div className="text-[9px] text-gray-400 mt-0.5">dont vous + {nbSitesDistants} site{nbSitesDistants > 1 ? 's' : ''} distant{nbSitesDistants > 1 ? 's' : ''}</div>
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-700 mb-2">Ce que vous pouvez faire :</p>
                <div className="space-y-2">
                  <div className="flex items-start gap-2.5">
                    <p className="text-xs text-gray-600"><strong>Modifier les réductions</strong> : Elles seront propagées automatiquement vers les sites distants.</p>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <p className="text-xs text-gray-600"><strong>Vue consolidée</strong> : Voir les élèves et paiements de tous les sites dans la section "Membres inter-sites".</p>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <p className="text-xs text-gray-600"><strong>Livret de caisse</strong> : Inclut automatiquement les données des sites distants.</p>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <p className="text-xs text-gray-600"><strong>Désactiver l'inter-site</strong> : Via le menu Actions. Les familles distantes avec moins de 2 élèves seront archivées.</p>
                  </div>
                </div>
              </div>

              <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                <AlertCircle size={12} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-[10px] text-amber-700">Les paiements sont toujours gérés localement sur chaque site. Vous ne pouvez pas payer pour les élèves des sites distants.</p>
              </div>
            </>
          )}

          {/* ═══════════════════════════════════════ */}
          {/* CAS 2 : Site distant — actif            */}
          {/* ═══════════════════════════════════════ */}
          {isDistant && (
            <>
              <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
                <p className="text-xs text-teal-700 leading-relaxed">
                  <strong>Cette famille est connectée au réseau inter-site.</strong> Le site de référence <strong>{interSiteInfo.site_reference}</strong> pilote les réductions famille. Vos élèves bénéficient automatiquement des mêmes réductions sauf si une règle personnalisée locale remplace la règle famille pour un élève précis.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-white border border-gray-200 rounded-lg text-center">
                  <div className="text-xl font-bold text-teal-700">{interSiteInfo.nb_membres_total}</div>
                  <div className="text-[10px] text-gray-500">membres total</div>
                </div>
                <div className="p-3 bg-white border border-gray-200 rounded-lg text-center">
                  <div className="text-sm font-bold text-teal-700">{interSiteInfo.site_reference}</div>
                  <div className="text-[10px] text-gray-500">site de référence</div>
                  <div className="text-[9px] text-gray-400 mt-0.5">{interSiteInfo.nb_sites} sites au total (dont vous)</div>
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-700 mb-2">Ce que vous pouvez faire :</p>
                <div className="space-y-2">
                  <div className="flex items-start gap-2.5">
                    <p className="text-xs text-gray-600"><strong>Ajouter/retirer des élèves</strong> : Vous gérez les élèves de votre site dans cette famille.</p>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <p className="text-xs text-gray-600"><strong>Gérer les paiements</strong> : Les paiements de vos élèves sont gérés localement sur votre site.</p>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <p className="text-xs text-gray-600"><strong>Livret de caisse</strong> : Inclut une vue d'ensemble des autres sites (à titre indicatif).</p>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-700 mb-2">Ce que vous ne pouvez pas faire :</p>
                <div className="space-y-2">
                  <div className="flex items-start gap-2.5">
                    <p className="text-xs text-gray-500">Modifier les réductions inter-site (géré par le site de référence)</p>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <p className="text-xs text-gray-500">Désactiver la liaison inter-site (géré par le site de référence)</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ═══════════════════════════════════════ */}
          {/* CAS 3 : Ancien référence — désactivé    */}
          {/* ═══════════════════════════════════════ */}
          {wasReference && (
            <>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-700 leading-relaxed">
                  <strong>La liaison inter-site a été désactivée.</strong> Cette famille fonctionne maintenant en tant que famille locale indépendante. Les réductions restent en place pour vos élèves locaux.
                </p>
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-700 mb-2">Action disponible :</p>
                <div className="p-3 bg-white border border-teal-200 rounded-lg flex items-start gap-2.5">
                  <RotateCcw size={13} className="text-teal-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-gray-700"><strong className="text-teal-700">Re-lier au Hub Inter-Site</strong></p>
                    <p className="text-[10px] text-gray-500 mt-0.5">Accessible via le menu Actions. Cela réactivera la famille dans le Hub et les sites distants pourront à nouveau rattacher leurs élèves.</p>
                  </div>
                </div>
              </div>

              <div className="p-2.5 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
                <Info size={12} className="text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-[10px] text-blue-700">Les sites distants verront un message "En attente de re-liaison" jusqu'à ce que vous re-liiez au Hub. Après la re-liaison, ils pourront rattacher leurs élèves en un clic.</p>
              </div>
            </>
          )}

          {/* ═══════════════════════════════════════ */}
          {/* CAS 4 : Ancien distant — hub pas actif  */}
          {/* ═══════════════════════════════════════ */}
          {wasDistant && !hubActive && (
            <>
              <div className="p-3 bg-gray-100 border border-gray-200 rounded-lg">
                <p className="text-xs text-gray-600 leading-relaxed">
                  <strong>La liaison inter-site a été désactivée par le site de référence.</strong> Cette famille fonctionne maintenant en tant que famille locale indépendante. Vous ne pouvez pas réactiver la liaison vous-même.
                </p>
              </div>

              <div className="p-3 bg-white border border-gray-200 rounded-lg">
                <p className="text-xs font-semibold text-gray-700 mb-2">Que faire ?</p>
                <div className="space-y-2">
                  <div className="flex items-start gap-2.5">
                    <ArrowRight size={13} className="text-gray-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-gray-600"><strong>Attendre</strong> que le site de référence re-lie la famille au Hub. Quand il le fera, un bouton vert "Rattacher au Hub" apparaîtra automatiquement dans le menu Actions.</p>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <ArrowRight size={13} className="text-gray-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-gray-600"><strong>Contacter</strong> l'administrateur du site de référence si vous souhaitez que la liaison soit réactivée.</p>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <ArrowRight size={13} className="text-gray-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-gray-600"><strong>Continuer normalement</strong> : La famille reste active localement, les paiements et réductions existants ne sont pas impactés.</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ═══════════════════════════════════════ */}
          {/* CAS 5 : Ancien distant — hub actif      */}
          {/* ═══════════════════════════════════════ */}
          {wasDistant && hubActive && (
            <>
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-xs text-green-700 leading-relaxed">
                  <strong>Le site de référence a re-lié la famille au Hub !</strong> Vous pouvez maintenant rattacher vos élèves pour rétablir la connexion inter-site.
                </p>
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-700 mb-2">Action disponible :</p>
                <div className="p-3 bg-white border border-green-200 rounded-lg flex items-start gap-2.5">
                  <Network size={13} className="text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-gray-700"><strong className="text-green-700">Rattacher au Hub Inter-Site</strong></p>
                    <p className="text-[10px] text-gray-500 mt-0.5">Accessible via le menu Actions. Tous vos élèves locaux seront automatiquement inscrits dans le Hub en un seul clic. Aucune sélection manuelle nécessaire.</p>
                  </div>
                </div>
              </div>

              <div className="p-2.5 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
                <Info size={12} className="text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-[10px] text-blue-700">Après le rattachement, la famille retrouvera son fonctionnement inter-site normal : réductions partagées, vue consolidée et livret familial complet.</p>
              </div>
            </>
          )}

        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex-shrink-0">
          <button
            onClick={onClose}
            className={`w-full px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors ${
              isReference ? 'bg-[#246990] hover:bg-[#1f5a7a]' : 
              isDistant ? 'bg-teal-600 hover:bg-teal-700' :
              wasDistant && hubActive ? 'bg-green-600 hover:bg-green-700' :
              'bg-gray-500 hover:bg-gray-600'
            }`}
          >
            Compris
          </button>
        </div>
      </div>
    </div>
  );
};

export default GuideInterSiteDetail;
